import { useEffect, useState } from 'react';
import { FALLBACK_REEL_IDS } from '../../lib/embedFallbacks';
import Carousel from '../Carousel/Carousel';
import './InstagramReels.css';

interface Props {
  /** Layout:
   *   · `rail`     — vertical stack (default; right rail)
   *   · `grid`     — responsive 3-up grid (no scroll)
   *   · `carousel` — horizontal scroll-snap track with prev/next
   *                  buttons + "View all on Instagram →" CTA. Used
   *                  by the polls_open below-hero band. */
  layout?: 'rail' | 'grid' | 'carousel';
  /** Show the "From the feed · @lovinmalta on the trail" header +
   *  inline link. Default true. The polls_open band passes false —
   *  the carousel's own "View all on Instagram →" CTA below the
   *  track is the replacement link. */
  showHeader?: boolean;
}

export default function InstagramReels({
  layout = 'rail',
  showHeader = true,
}: Props = {}) {
  const [reels, setReels] = useState<string[]>(FALLBACK_REEL_IDS);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/site-embeds', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { embeds?: { instagram?: string[] } } | null) => {
        if (cancelled) return;
        const ids = data?.embeds?.instagram ?? [];
        if (ids.length > 0) setReels(ids);
      })
      .catch(() => {
        // Silent — keep fallback list.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sectionClass =
    layout === 'rail' ? 'reels' : 'reels reels--grid'; // grid + carousel
                                                       // share the
                                                       // header-less
                                                       // wrapper.
  const listClass =
    layout === 'grid'
      ? 'reels__list reels__list--grid'
      : 'reels__list';

  return (
    <section className={sectionClass} aria-label="Latest Instagram reels from Lovin Malta">
      {showHeader && (
        <header className="reels__header">
          <p className="reels__kicker">From the feed</p>
          <h3 className="reels__title">@lovinmalta on the trail</h3>
          <a
            href="https://www.instagram.com/lovinmalta/"
            target="_blank"
            rel="noopener noreferrer"
            className="reels__link"
          >
            View all on Instagram →
          </a>
        </header>
      )}

      {layout === 'carousel' ? (
        <Carousel
          // 340 px width × .reels__item aspect-ratio 9/11 ≈ 415 px
          // tall — bigger embed footprint than 280 was, and at
          // gap 22 px four items fit the band's ~1436 px inner
          // width cleanly (4×340 + 3×22 = 1426 ≈ full row) so
          // there's no empty space on the right.
          itemMin="340px"
          gap="22px"
          ariaLabel="Latest Instagram reels"
          moreLink={{
            href: 'https://www.instagram.com/lovinmalta/',
            label: 'View all on Instagram →',
          }}
        >
          {reels.map((id, i) => (
            <div key={id} className="reels__item">
              <iframe
                src={`https://www.instagram.com/reel/${id}/embed/`}
                title={`Lovin Malta reel ${i + 1}`}
                className="reels__iframe"
                loading="lazy"
                scrolling="no"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          ))}
        </Carousel>
      ) : (
        <ul className={listClass}>
          {reels.map((id, i) => (
            <li key={id} className="reels__item">
              <iframe
                src={`https://www.instagram.com/reel/${id}/embed/`}
                title={`Lovin Malta reel ${i + 1}`}
                className="reels__iframe"
                loading="lazy"
                scrolling="no"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
